import React, { useState, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import Bluebird from 'bluebird';
import { Box, IconButton, InputAdornment, Tooltip } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { OnChange } from 'react-final-form-listeners';
import { Form } from 'react-final-form';
import { Capacitor } from '@capacitor/core';
import { DividerLine } from './DividerLine';
import { GoogleLogoIcon, AppleLogoIcon } from '../../assets/SvgIcons';
import routes from '../../routes';
import ButtonWithLoading from '../../components/ButtonWithLoading';
import * as authApi from '../../api/auth';
import * as authService from '../../services/auth';
import errorMessages from '../../assets/errorMessages';
import FABContext from '../../context/FABContext/FABContext';
import { useFormStyles } from './authStyles';
import { setFABActionsForCurrentlyLoggedInUser } from '../../services/auth';
import RFFTextField from '../../components/Common/RFFTextField';
import * as errorsHelper from '../../helpers/errors';

const validateForm = values => {
  const { email = '', password = '' } = values;
  const errors = {};

  if (email.length === 0) {
    errors.email = 'Required';
  }

  if (password.length === 0) {
    errors.password = 'Required';
  }

  return errors;
};
const LoginForm = () => {
  const history = useHistory();
  const [isSubmitting, setSubmitting] = useState(false);
  const fabContext = useContext(FABContext);
  const authStyles = useFormStyles();
  const [showPassword, setShowPassword] = useState(false);
  // Apple sign-in is only offered in the native iOS app; Google is offered on all platforms.
  const isIos = Capacitor.getPlatform() === 'ios';

  // both onSubmit and handleLoginWithGoogle return promises
  // but they return different values, and the login is in different
  // states. onSubmit returns the user with role assignments
  // handleLoginWithGoogle returns just the auth0 user

  // note how this form never results in a redirect. the 'success' of
  // this form is hidden in the auth service methods.
  const onSubmit = values => {
    const { email, password } = values;

    setSubmitting(true);

    return Bluebird
      .try(() => authApi.login(email, password))
      .then(res => res.json())
      .then(json => authService.handleAuthentication(history, json.data))
      .then(() => setFABActionsForCurrentlyLoggedInUser(fabContext))
      .catch(err => {
        setSubmitting(false);

        if (err.code === errorMessages.ERR_SYSTEM_NOT_AVAILABLE.code) {
          return history.push(routes.systemNotAvailable);
        }

        errorsHelper.handleError(err);
        return {
          password: 'Invalid email or password',
        };
      });
  };

  const handleLoginWithGoogle = () => authService.authorizeWithGoogle({}, fabContext);
  const handleLoginWithApple = () => authService.authorizeWithApple({}, fabContext);

  const resetFormSubmitErrors = form => {
    const emailValue = form.getFieldState('email').value;
    const passwordValue = form.getFieldState('password').value;
    form.reset({ email: emailValue, password: passwordValue });
  };

  const handleClickShowPassword = () => setShowPassword(!showPassword);

  return (
    <>
      <Form
        onSubmit={(values) => onSubmit(values)}
        validate={validateForm}
        render={({ handleSubmit, pristine, invalid, dirtySinceLastSubmit, submitting, form }) => (
          <form style={authStyles.form} onSubmit={handleSubmit}>
            <Box sx={authStyles.input}>
              <RFFTextField
                margin="normal"
                name="email"
                disabled={isSubmitting}
                type="email"
                variant="outlined"
                fullWidth
                placeholder="Email Address"
                autoComplete="email"
                data-testid="login-email-input"
              />

            </Box>
            <OnChange name="email">
              {() => {
                if (form.getFieldState('password').submitError || form.getFieldState('email').submitError) {
                  resetFormSubmitErrors(form);
                }
              }}
            </OnChange>

            <Box sx={authStyles.input}>
              <RFFTextField
                margin="normal"
                name="password"
                disabled={isSubmitting}
                type={showPassword ? 'text' : 'password'}
                variant="outlined"
                fullWidth
                placeholder="Password"
                autoComplete="current-password"
                data-testid="login-password-input"
                InputProps={{
                  endAdornment: (
                    <Tooltip title={showPassword ? 'Hide password' : 'Show password'}>
                      <InputAdornment position="end">
                        <IconButton onClick={handleClickShowPassword} size="large">
                          {showPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    </Tooltip>
                  ),
                }}
              />
            </Box>
            <OnChange name="password">
              {() => {
                if (form.getFieldState('password').submitError || form.getFieldState('email').submitError) {
                  resetFormSubmitErrors(form);
                }
              }}
            </OnChange>

            <ButtonWithLoading
              sx={authStyles.submitButton}
              fullWidth
              type="submit"
              isLoading={submitting || isSubmitting}
              disabled={pristine || (invalid && !dirtySinceLastSubmit)}
              data-testid="login-submit-button"
            >
              Login
            </ButtonWithLoading>
          </form>
        )}
      />

      <DividerLine />

      {isIos && (
        <ButtonWithLoading
          startIcon={<AppleLogoIcon width={36} height={36} />}
          sx={authStyles.appleButton}
          fullWidth
          onClick={() => handleLoginWithApple()}
          isLoading={isSubmitting}
          type="submit"
        >
          Sign in with Apple
        </ButtonWithLoading>
      )}

      <ButtonWithLoading
        startIcon={<GoogleLogoIcon width={36} height={36} />}
        sx={authStyles.googleButton}
        fullWidth
        onClick={() => handleLoginWithGoogle()}
        isLoading={isSubmitting}
        type="submit"
      >
        Login with google
      </ButtonWithLoading>
    </>
  );
};

export default LoginForm;
